import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, ArrowRight, Users, Megaphone, Palette, Music, Check, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";

const C = { bg: "#08080c", surface: "#101018", magenta: "#ff0055", cyan: "#00f0ff", yellow: "#ffe600" };

const TIERS = [
  { name: "Title Sponsor", price: "$25,000", accent: C.magenta, perks: ["Exclusive \"presented by\" naming rights", "Headline logo on main stage & step-and-repeat", "Top placement on site, tickets & all marketing", "10 VIP Spider-Society passes", "Premium activation booth + full-page program feature"] },
  { name: "Corporate", price: "$10,000", accent: C.cyan, perks: ["Large logo on stage & website sponsor wall", "6 VIP Spider-Society passes", "10x10 branded activation booth", "Program logo + social features", "On-stage shout-outs"] },
  { name: "Community Partner", price: "$4,500", accent: C.yellow, perks: ["Logo on website wall & program", "4 General Admission tickets", "Shared community activation table", "Social media thank-you post"] },
  { name: "Digital", price: "$500", accent: C.cyan, perks: ["Logo auto-displayed on website sponsor wall", "Clickable link from sponsors section", "Social media shout-out", "Listed in the digital program"] },
];

const STATS = [
  { n: "3,000+", l: "Projected Attendance" },
  { n: "6 hrs", l: "Prime Sat Evening" },
  { n: "65%", l: "Ages 18–34" },
  { n: "100%", l: "All-Ages / Non-Alcoholic" },
  { n: "50K+", l: "Projected Social Reach" },
  { n: "40+", l: "Vendors & Creators" },
];

function Halftone({ color = "#ff0055", opacity = 0.18 }) {
  return (
    <div style={{
      position: "absolute", inset: 0, opacity,
      backgroundImage: `radial-gradient(${color} 1.4px, transparent 1.6px)`,
      backgroundSize: "18px 18px", pointerEvents: "none",
    }} />
  );
}

const Slide = ({ children, refCb, style }) => (
  <div
    ref={refCb}
    className="deck-slide relative w-full overflow-hidden rounded-2xl border border-white/10"
    style={{ aspectRatio: "16 / 9", background: C.bg, ...style }}
  >
    {children}
  </div>
);

export default function SponsorDeck() {
  const slides = useRef([]);
  const [busy, setBusy] = useState(false);
  const setRef = (i) => (el) => { slides.current[i] = el; };

  const download = async () => {
    setBusy(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const els = slides.current.filter(Boolean);
      let pdf = null;
      for (let i = 0; i < els.length; i++) {
        const canvas = await html2canvas(els[i], { scale: 2, backgroundColor: C.bg, useCORS: true, logging: false });
        const img = canvas.toDataURL("image/jpeg", 0.92);
        const w = canvas.width, h = canvas.height;
        if (!pdf) pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [w, h], compress: true });
        else pdf.addPage([w, h], "landscape");
        pdf.addImage(img, "JPEG", 0, 0, w, h);
      }
      pdf.save("Spider-Verse-Denver-2026-Sponsorship-Deck.pdf");
      toast.success("Deck downloaded! THWIP!");
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-24 md:pt-28 pb-20">
      {/* Toolbar */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono2 text-xs tracking-[0.3em] text-[#00f0ff] uppercase mb-2">Partner With Us</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">Sponsorship <span className="chromatic">Deck</span></h1>
        </div>
        <div className="flex gap-3">
          <button data-testid="deck-download-btn" onClick={download} disabled={busy}
            className="inline-flex items-center gap-2 bg-[#ff0055] text-white font-head font-bold uppercase px-6 py-3 rounded-full glow-magenta hover:scale-105 transition-transform disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} {busy ? "Building PDF…" : "Download PDF"}
          </button>
          <Link data-testid="deck-apply-btn" to="/get-involved" className="inline-flex items-center gap-2 glass-chroma font-head font-bold uppercase px-6 py-3 rounded-full hover:scale-105 transition-transform">
            Apply <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Deck */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 space-y-8" data-testid="deck-slides">

        {/* 1 — COVER */}
        <Slide refCb={setRef(0)}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(120% 120% at 100% 0%, ${C.magenta}44 0%, transparent 45%), radial-gradient(120% 120% at 0% 100%, ${C.cyan}33 0%, transparent 45%)` }} />
          <Halftone color={C.magenta} opacity={0.15} />
          <div className="relative h-full flex flex-col justify-between p-8 md:p-12">
            <div className="flex items-center justify-between">
              <span className="font-display text-2xl md:text-3xl chromatic">SPIDER·VERSE DENVER</span>
              <span className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-white/60">SPONSORSHIP · 2026</span>
            </div>
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="badge-skew bg-[#ffe600] text-black px-3 py-1 border-2 border-black text-sm"><span>SAT · SEPT 19 · 2026</span></span>
                <span className="badge-skew bg-[#00f0ff] text-black px-3 py-1 border-2 border-black text-sm"><span>ALL-AGES</span></span>
              </div>
              <h2 className="font-display uppercase leading-[0.85] text-4xl md:text-7xl">
                <span className="text-white">Anyone Can</span><br />
                <span className="chromatic">Wear The Mask.</span>
              </h2>
              <p className="font-head text-white/70 mt-4 max-w-2xl text-sm md:text-base">
                Denver's premier all-ages multiverse art, music &amp; cosplay experience — and a bold canvas for your brand.
              </p>
            </div>
            <div className="flex items-center gap-6 font-head text-xs md:text-sm text-white/60">
              <span className="flex items-center gap-2"><MapPin size={14} className="text-[#ff0055]" /> Truss House @ RiNo ArtPark, Denver CO</span>
              <span className="flex items-center gap-2"><Calendar size={14} className="text-[#00f0ff]" /> 4:00–10:00 PM MDT</span>
            </div>
          </div>
        </Slide>

        {/* 2 — THE EXPERIENCE */}
        <Slide refCb={setRef(1)}>
          <Halftone color={C.cyan} opacity={0.1} />
          <div className="relative h-full p-8 md:p-12 flex flex-col">
            <div className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-[#ff0055] uppercase mb-2">01 — The Experience</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-6">Six Hours, Infinite Dimensions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
              {[
                { icon: Music, t: "Live Spider-Punk", d: "Jole & DJ ciphers headline the night.", c: C.magenta },
                { icon: Users, t: "Cosplay Runway", d: "5-category costume contest & catwalk.", c: C.cyan },
                { icon: Palette, t: "Comic Alley", d: "Live graffiti battles & print artists.", c: C.yellow },
                { icon: Megaphone, t: "Mocktail Bar", d: "100% non-alcoholic + food trucks.", c: C.cyan },
              ].map((x) => (
                <div key={x.t} className="rounded-xl p-4 border border-white/10" style={{ background: C.surface }}>
                  <x.icon size={22} style={{ color: x.c }} />
                  <div className="font-head font-bold mt-3 text-sm md:text-base">{x.t}</div>
                  <div className="font-head text-white/60 text-xs md:text-sm mt-1">{x.d}</div>
                </div>
              ))}
            </div>
          </div>
        </Slide>

        {/* 3 — BY THE NUMBERS */}
        <Slide refCb={setRef(2)}>
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${C.surface} 0%, ${C.bg} 60%)` }} />
          <Halftone color={C.yellow} opacity={0.08} />
          <div className="relative h-full p-8 md:p-12 flex flex-col">
            <div className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-[#00f0ff] uppercase mb-2">02 — The Audience</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-6">By The Numbers</h2>
            <div className="grid grid-cols-3 gap-4 md:gap-6 flex-1 content-center">
              {STATS.map((s) => (
                <div key={s.l} className="text-center">
                  <div className="font-display text-4xl md:text-6xl" style={{ color: C.cyan }}>{s.n}</div>
                  <div className="font-head text-white/60 text-[11px] md:text-sm uppercase tracking-wide mt-1">{s.l}</div>
                </div>
              ))}
            </div>
            <p className="font-head text-white/40 text-[10px] md:text-xs mt-4">*Projected figures for the 2026 inaugural Denver edition.</p>
          </div>
        </Slide>

        {/* 4 — WHY SPONSOR */}
        <Slide refCb={setRef(3)}>
          <Halftone color={C.magenta} opacity={0.1} />
          <div className="relative h-full p-8 md:p-12 flex flex-col">
            <div className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-[#ffe600] uppercase mb-2">03 — Why Partner</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-6">Put Your Brand At The Center</h2>
            <div className="grid md:grid-cols-2 gap-4 flex-1 content-start">
              {[
                "Reach a passionate, highly engaged all-ages pop-culture community.",
                "Family-friendly, brand-safe environment — 100% non-alcoholic.",
                "Immersive activation opportunities beyond a static logo.",
                "Deep local Denver / RiNo Art District roots & press coverage.",
                "Authentic alignment with art, music, inclusion & creativity.",
                "Year-round digital presence on our site & social channels.",
              ].map((x) => (
                <div key={x} className="flex gap-3 rounded-xl p-4 border border-white/10" style={{ background: C.surface }}>
                  <Check size={18} style={{ color: C.magenta }} className="shrink-0 mt-0.5" />
                  <span className="font-head text-sm md:text-base text-white/80">{x}</span>
                </div>
              ))}
            </div>
          </div>
        </Slide>

        {/* 5 — TIERS */}
        <Slide refCb={setRef(4)}>
          <Halftone color={C.cyan} opacity={0.08} />
          <div className="relative h-full p-6 md:p-10 flex flex-col">
            <div className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-[#ff0055] uppercase mb-1">04 — Investment</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-4">Sponsorship Tiers</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              {TIERS.map((t) => (
                <div key={t.name} className="rounded-xl p-4 border flex flex-col" style={{ background: C.surface, borderColor: `${t.accent}55` }}>
                  <div className="font-head font-bold text-xs md:text-sm uppercase" style={{ color: t.accent }}>{t.name}</div>
                  <div className="font-display text-2xl md:text-3xl mt-1 mb-2 text-white">{t.price}</div>
                  <ul className="space-y-1.5">
                    {t.perks.map((p) => (
                      <li key={p} className="flex gap-1.5 font-head text-[10px] md:text-xs text-white/75 leading-snug">
                        <Check size={12} style={{ color: t.accent }} className="shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Slide>

        {/* 6 — CONTACT */}
        <Slide refCb={setRef(5)}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(120% 120% at 0% 0%, ${C.cyan}33 0%, transparent 45%), radial-gradient(120% 120% at 100% 100%, ${C.magenta}44 0%, transparent 45%)` }} />
          <Halftone color={C.magenta} opacity={0.14} />
          <div className="relative h-full p-8 md:p-12 flex flex-col items-center justify-center text-center">
            <div className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-[#00f0ff] uppercase mb-3">Let's Build It Together</div>
            <h2 className="font-display text-4xl md:text-7xl uppercase leading-none">Enter The <span className="chromatic">Spider-Verse.</span></h2>
            <p className="font-head text-white/70 mt-4 max-w-xl text-sm md:text-base">
              Reserve your tier or design a custom activation. We'll tailor a package to your brand goals.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center">
              <span className="font-head font-bold text-base md:text-xl text-[#ffe600]">dondrae@eventism.com</span>
              <span className="hidden sm:block text-white/30">·</span>
              <span className="font-head text-white/60 text-sm md:text-base">spiderverse.denver</span>
            </div>
            <div className="mt-6 badge-skew bg-[#ff0055] text-white px-4 py-1.5 border-2 border-black"><span>THWIP! LET'S PARTNER</span></div>
          </div>
        </Slide>
      </div>

      {/* Bottom CTA */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 mt-10 text-center">
        <p className="font-head text-white/50 text-sm mb-4">Ready to lock in your tier?</p>
        <Link to="/get-involved" className="inline-flex items-center gap-2 bg-[#ffe600] text-black font-head font-bold uppercase px-8 py-3.5 rounded-full hover:scale-105 transition-transform">
          Apply to Sponsor <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}
